import { useRef, useState, useEffect } from "react";
import NepaliDatePicker from '@zener/nepali-datepicker-react';
import '@zener/nepali-datepicker-react/index.css';
import "./GrainForm.css";

export default function GrainForm({
    form,
    setForm,
    grains,
    onSubmit,
    onShowGrainModal,
    submitting,
    onEditGrain,
    onDeleteGrain
}) {
    const firstRef = useRef();
    const [showGrainDropdown, setShowGrainDropdown] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown')) {
                setShowGrainDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleGrainSelect = (grainId) => {
        setForm(prev => ({ ...prev, grainId }));
        setShowGrainDropdown(false);
    };

    const handleGrainEdit = (grain, e) => {
        e.stopPropagation();
        setShowGrainDropdown(false);
        if (onEditGrain) {
            onEditGrain(grain);
        }
    };

    const handleGrainDelete = (grain, e) => {
        e.stopPropagation();
        setShowGrainDropdown(false);
        if (onDeleteGrain) {
            onDeleteGrain(grain);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let updated = { ...form, [name]: value };

        if (name === "grainId" && value === "create_grain") {
            onShowGrainModal(true);
            return;
        }

        const qty = +updated.quantity || 0;
        const finalPrice = +updated.finalPrice || 0;
        if (qty > 0 && finalPrice > 0) {
            updated.rate = (finalPrice / qty).toFixed(2);
        } else {
            updated.rate = "";
        }

        setForm(updated);
    };

    return (
        <div className="grain-form-inline">
            <h6 className="grain-form-title">Add New Grain Transaction</h6>
            <form onSubmit={onSubmit} className="row g-3 mb-4 align-items-end">
                <div className="col-md-2">
                    <select
                        ref={firstRef}
                        name="buyOrSell"
                        className="form-select"
                        value={form.buyOrSell || ""}
                        onChange={handleChange}
                        required
                        disabled={submitting}
                    >
                        <option value="">Buy/Sell</option>
                        <option value="Buy">Buy</option>
                        <option value="Sell">Sell</option>
                    </select>
                </div>

                <div className="col-md-3">
                    <div className="dropdown">
                        <button
                            className="btn btn-outline-secondary dropdown-toggle w-100 text-start"
                            type="button"
                            onClick={() => setShowGrainDropdown(!showGrainDropdown)}
                            disabled={submitting}
                        >
                            {form.grainId ? grains.find(g => g._id === form.grainId)?.grainName || "Select Grain" : "Select Grain"}
                        </button>
                        {showGrainDropdown && (
                            <ul className="dropdown-menu show w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {grains.map(g => (
                                    <li key={g._id} className="d-flex align-items-center px-2 py-1">
                                        <button
                                            className="btn btn-link text-decoration-none flex-grow-1 text-start p-0"
                                            onClick={() => handleGrainSelect(g._id)}
                                        >
                                            {g.grainName}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary ms-1"
                                            onClick={(e) => handleGrainEdit(g, e)}
                                            title="Edit grain"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger ms-1"
                                            onClick={(e) => handleGrainDelete(g, e)}
                                            title="Delete grain"
                                        >
                                            🗑️
                                        </button>
                                    </li>
                                ))}
                                <li className="px-2 py-1">
                                    <button
                                        className="btn btn-link text-primary text-decoration-none w-100 text-start p-0"
                                        onClick={() => {
                                            setShowGrainDropdown(false);
                                            onShowGrainModal(true);
                                        }}
                                    >
                                        + New Grain
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>

                {/* QUANTITY + kg */}
                <div className="col-md-2">
                    <div className="input-group">
                        <input
                            type="number"
                            name="quantity"
                            placeholder="Qty"
                            className="form-control"
                            value={form.quantity || ""}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                        />
                        <span className="input-group-text">kg</span>
                    </div>
                </div>

                {/* RATE + ₹/kg */}
                <div className="col-md-2">
                    <div className="input-group">
                        <input
                            type="number"
                            name="rate"
                            placeholder="Rate"
                            className="form-control"
                            value={form.rate || ""}
                            readOnly
                        />
                        <span className="input-group-text">₹/kg</span>
                    </div>
                </div>

                {/* FINAL PRICE + ₹ */}
                <div className="col-md-2">
                    <div className="input-group">
                        <input
                            type="number"
                            name="finalPrice"
                            placeholder="Final Price"
                            className="form-control"
                            value={form.finalPrice || ""}
                            onChange={handleChange}
                            required
                            disabled={submitting}
                        />
                        <span className="input-group-text">₹</span>
                    </div>
                </div>

                <div className="col-md-2">
                    <div className="position-relative">
                        <NepaliDatePicker
                            value={form.time ? new Date(form.time) : null}
                            onChange={(date) => {
                                if (date) {
                                    // Convert NepaliDate to regular Date
                                    const regularDate = date.toADasDate ? date.toADasDate() : date;
                                    setForm(prev => ({ ...prev, time: regularDate.toISOString() }));
                                } else {
                                    setForm(prev => ({ ...prev, time: null }));
                                }
                            }}
                            placeholder="Select BS Date"
                            className="form-control"
                            disabled={submitting}
                            type="BS"
                        />
                    </div>
                </div>

                <div className="col-md-3">
                    <input
                        type="text"
                        name="remarks"
                        placeholder="Remarks (optional)"
                        className="form-control"
                        value={form.remarks || ""}
                        onChange={handleChange}
                        disabled={submitting}
                    />
                </div>

                <div className="col-md-12 text-end">
                    <button
                        className="btn btn-success"
                        type="submit"
                        disabled={submitting}
                    >
                        {submitting ? "Adding..." : "Add Transaction"}
                    </button>
                </div>
            </form>
        </div>
    );
}